import { FC, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HomeIcon, ChartBarIcon, ShareIcon } from "@heroicons/react/24/outline";

const Header: FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const isHome = location.pathname === "/";
	const [shareLabel, setShareLabel] = useState<"Share" | "Copied" | "Failed">("Share");

	const resetShareLabel = () => {
		window.setTimeout(() => setShareLabel("Share"), 1800);
	};

	const handleShare = async () => {
		const shareUrl = window.location.href;

		try {
			if (navigator.share) {
				await navigator.share({
					title: "Inside Edge",
					text: "Check out this Inside Edge view",
					url: shareUrl
				});
				return;
			}

			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(shareUrl);
				setShareLabel("Copied");
				resetShareLabel();
				return;
			}

			const fallbackInput = document.createElement("input");
			fallbackInput.value = shareUrl;
			document.body.appendChild(fallbackInput);
			fallbackInput.select();
			document.execCommand("copy");
			document.body.removeChild(fallbackInput);

			setShareLabel("Copied");
			resetShareLabel();
		} catch {
			setShareLabel("Failed");
			resetShareLabel();
		}
	};

	return (
		<header className="sticky top-0 z-50 backdrop-blur-xl bg-[#081223]/85 border-b border-slate-700/70 shadow-2xl">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-16 sm:h-20 gap-2 sm:gap-4">
					<div className="flex items-center cursor-pointer min-w-0" onClick={() => navigate("/")}>
						<div className="relative shrink-0">
							<div className="w-9 h-9 sm:w-11 sm:h-11 pitch-gradient rounded-xl flex items-center justify-center shadow-lg">
								<span className="text-white font-bold text-base sm:text-lg tracking-wider">IE</span>
							</div>
							<div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full"></div>
						</div>
						<div className="ml-3 min-w-0">
							<h1 className="text-xl sm:text-3xl leading-none text-white truncate">Inside Edge</h1>
							<p className="hidden sm:block text-[11px] text-slate-300 uppercase tracking-[0.18em]">
								Cricket Intelligence
							</p>
						</div>
					</div>

					<nav className="flex items-center space-x-2 sm:space-x-3 shrink-0">
						{/* <div className="hidden md:flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/70 px-3 py-1 text-xs text-slate-300">
							<BoltIcon className="h-4 w-4 text-amber-300" />
							<span>Live Data Engine</span>
						</div> */}
						<button
							onClick={handleShare}
							className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-lg text-slate-200 border border-slate-600/80 hover:text-white hover:bg-slate-800 transition-colors duration-150"
						>
							<ShareIcon className="h-5 w-5 sm:h-6 sm:w-6" />
							<span className="hidden sm:inline font-semibold text-base">{shareLabel}</span>
						</button>

						<button
							onClick={() => navigate("/")}
							className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-lg transition-colors duration-150 ${
								isHome
									? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
									: "text-slate-300 border border-transparent hover:text-white hover:bg-slate-800"
							}`}
						>
							<HomeIcon className="h-5 w-5 sm:h-6 sm:w-6" />
							<span className="hidden sm:inline font-semibold text-base">Home</span>
						</button>

						{!isHome && (
							<div className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-orange-500/20 text-orange-200 rounded-lg border border-orange-300/40">
								<ChartBarIcon className="h-5 w-5" />
								<span className="font-semibold">Command Center</span>
							</div>
						)}
					</nav>
				</div>
			</div>
		</header>
	);
};

export default Header;
